import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { lineString } from "@turf/helpers";
import { booleanIntersects } from "@turf/boolean-intersects";
import { DRAWING_MODES } from '../../utilities/constants';
import { iosCheck } from '../../utilities/stringUtil';

const GoogleMapComponent = ({
  latitude,
  longitude,
  initialRadius,
  polygonCoordinates,
  postalCode,
  drawingMode,
  onCircleChange,
  onPolygonChange,
  onMapLoad,
  disabled,
}) => {
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const [currentZoom, setCurrentZoom] = useState(15);
  const circleRef = useRef(null);
  const polygonRef = useRef(null);
  const polygonPathRef = useRef(polygonCoordinates || []);
  const infoWindowRef = useRef(null);

  const defaultRadius = 100;

  const circleState = useRef({ center: null, radius: null });

  const initMap = () => {
    const center = { lat: latitude || 35.6581196, lng: longitude || 139.7413643 };
    const localMap = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: currentZoom,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false,
    });

    googleMapRef.current = localMap;
    onMapLoad(localMap);

    if (drawingMode === DRAWING_MODES.circle.valueName) {
      initializeCircle(center, localMap);
    } else {
      initializePolygon();
    }

    localMap.addListener('zoom_changed', () => {
      setCurrentZoom(localMap.getZoom());
    });

    localMap.addListener('click', (e) => {
      if (drawingMode === DRAWING_MODES.circle.valueName) {
        if (circleRef.current) {
          circleRef.current.setMap(null);
        }
        addCircle(e.latLng, defaultRadius, localMap);
      } else {
        addPolygonPoint(e.latLng);
      }
    });

    const overlayButton = document.createElement('button');
    overlayButton.textContent = 'ポリゴンをクリア';
    overlayButton.classList.add('btn', 'reset-polygon');
    mapRef.current.appendChild(overlayButton);

    overlayButton.addEventListener('click', resetPolygon);
  };

  const initializeCircle = (center) => {
    if (latitude && longitude && initialRadius) {
      addCircle(center, initialRadius);
    } else if (postalCode) {
      lookupLatLngFromPostalCode(postalCode, center);
    } else {
      addCircle(center, defaultRadius);
    }
  };

  const initializePolygon = () => {
    if (polygonPathRef.current) {
      const normalizedPath = normalizeCoordinates(polygonPathRef.current);
      addPolygon(normalizedPath, true);
    } 
  };

  const lookupLatLngFromPostalCode = (postalCode, defaultCenter) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: postalCode }, (results, status) => {
      if (status === 'OK') {
        const loc = results[0].geometry.location;
        googleMapRef.current.setCenter(loc);
        addCircle(loc, defaultRadius);
      } else {
        googleMapRef.current.setCenter(defaultCenter);
        addCircle(defaultCenter, defaultRadius);
      }
    });
  };

  const addCircle = (center, radius) => {
    const newCircle = new window.google.maps.Circle({
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      map: googleMapRef.current,
      center,
      radius,
      draggable: true,
      editable: true,
    });

    circleRef.current = newCircle;
    circleState.current = { center, radius };
    onCircleChange(newCircle.getCenter().toJSON(), newCircle.getRadius());

    newCircle.addListener('center_changed', () => {
      onCircleChange(newCircle.getCenter().toJSON(), newCircle.getRadius());
    });

    newCircle.addListener('radius_changed', () => {
      onCircleChange(newCircle.getCenter().toJSON(), newCircle.getRadius());
    });
  };

  const addPolygon = (path, setCenter = false) => {
    if (circleRef.current) {
      circleRef.current.setMap(null);
      circleRef.current = null;
    }
    const newPolygon = new window.google.maps.Polygon({
      paths: path,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      draggable: true,
      editable: true,
    });

    polygonRef.current = newPolygon;
    polygonRef.current.setMap(googleMapRef.current);

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(coord => bounds.extend(coord));
    if (setCenter) {
      googleMapRef.current.fitBounds(bounds);
    }

    window.google.maps.event.addListener(newPolygon, "rightclick", (event) => {
      const path = newPolygon.getPath();
      let closestVertexIndex = null;
      let closestDistance = Number.MAX_VALUE;

      path.forEach((point, index) => {
        const distance = window.google.maps.geometry.spherical.computeDistanceBetween(event.latLng, point);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestVertexIndex = index;
        }
      });

      if (closestVertexIndex !== null && closestDistance < 20) {
        path.removeAt(closestVertexIndex);
        updatePolygonPath();
      }
    });

    if (iosCheck()) {
      enableLongPressOnPolygon();
    }

    // ポリゴンの初期化が遅れている対応
    const idleListener = googleMapRef.current.addListener("idle", () => {
      attachPolygonDragListener();
      attachPolygonListeners();
      updatePolygonPath();
      window.google.maps.event.removeListener(idleListener);
    });
  };
  
  const handlePolygonVertexDeletion = (event) => {
    const path = polygonRef.current.getPath();
    let closestVertexIndex = null;
    let closestDistance = Number.MAX_VALUE;
  
    path.forEach((point, index) => {
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
        event.latLng,
        point
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestVertexIndex = index;
      }
    });
  
    if (closestVertexIndex !== null && closestDistance < 20) {
      path.removeAt(closestVertexIndex);
      polygonPathRef.current = path.getArray();
      updatePolygonPath();
    }
  };

  const enableLongPressOnPolygon = () => {
    let longPressTimer = null;
    let isDraggingVertex = false;
  
    window.google.maps.event.addListener(polygonRef.current, "mousedown", (event) => {
      isDraggingVertex = false;
      event.domEvent.stopPropagation();
      event.domEvent.preventDefault();
  
      longPressTimer = setTimeout(() => {
        if (!isDraggingVertex) {
          handlePolygonVertexDeletion(event);
        }
      }, 800);
    });
  
    window.google.maps.event.addListener(polygonRef.current, "mousemove", () => {
      isDraggingVertex = true;
      clearTimeout(longPressTimer);
    });
  
    window.google.maps.event.addListener(polygonRef.current, "mouseup", () => {
      clearTimeout(longPressTimer);
      isDraggingVertex = false;
    });
  
    window.google.maps.event.addListener(polygonRef.current, "mouseout", () => {
      clearTimeout(longPressTimer);
      isDraggingVertex = false;
    });
  };

  const attachPolygonListeners = () => {
    if (!polygonRef.current) return;

    const path = polygonRef.current.getPath();
  
    if (!path.setAtListener) {
      path.setAtListener = window.google.maps.event.addListener(path, 'set_at', (index, previous) => {
        const coordinates = getPathCoordinates();
    
        if (!polygonPathRef.current.skipValidation && doesPolygonSelfIntersect(coordinates)) {
          hideCurrentUndoButton();
          path.setAt(index, previous);
        } else {
          polygonPathRef.current = path.getArray();
          updatePolygonPath();
        }
      });
    }
  
    if (!path.insertAtListener) {
      path.insertAtListener = window.google.maps.event.addListener(path, 'insert_at', (index) => {
        const coordinates = getPathCoordinates();
        if (doesPolygonSelfIntersect(coordinates)) {
          hideCurrentUndoButton();
          path.removeAt(index);
        } else {
          polygonPathRef.current = path.getArray();
          updatePolygonPath();
        }
      });
    }
  
    if (!path.removeAtListener) {
      path.removeAtListener = window.google.maps.event.addListener(path, 'remove_at', (index, previous) => {
        const coordinates = getPathCoordinates();

        if (doesPolygonSelfIntersect(coordinates)) {
          polygonPathRef.current = path.getArray();
          polygonRef.current.setOptions({ editable: false });
    
          // 一時的に削除したままポリゴンを表示し、200ms後で復元する
          setTimeout(() => {
            polygonRef.current.setOptions({ editable: true });
            path.insertAt(index, previous);
            polygonPathRef.current = path.getArray();
          }, 200);
        } else {
          polygonPathRef.current = path.getArray();
          updatePolygonPath();
        }
      });
    }
  }

  const normalizeCoordinates = (path) => {
    return path.map((point) => {
      if (typeof point.lat === 'function' && typeof point.lng === 'function') {
        return { lat: point.lat(), lng: point.lng() };
      }
      return point;
    });
  };

  const addPolygonPoint = (latLng) => {
    const normalizedPath = normalizeCoordinates(polygonPathRef.current);
    const newPath = [...normalizedPath, { lat: latLng.lat(), lng: latLng.lng() }];
    const coordinates = newPath.map((point) => [point.lng, point.lat]);
  
    if (polygonRef.current && doesPolygonSelfIntersect(coordinates)) {  

      // 一時的に削除したままポリゴンを表示し、200ms後で復元する
      polygonPathRef.current = newPath;
      polygonRef.current.setPath(newPath);
      hideCurrentUndoButton();
      polygonRef.current.setOptions({ editable: false });
  
      setTimeout(() => {
        polygonRef.current.setOptions({ editable: true });
        polygonPathRef.current = normalizedPath; 
        polygonRef.current.setPath(normalizedPath);
        attachPolygonListeners();
      }, 200);
  
      return;
    }
  
    polygonPathRef.current = newPath;
  
    if (polygonRef.current) {
      polygonRef.current.setPath(newPath);
      attachPolygonListeners();
    } else {
      addPolygon(newPath);
    }
  
    updatePolygonPath();
  };

  const getPathCoordinates = () => {
    const path = polygonRef.current.getPath();
    return path.getArray().map((latLng) => [latLng.lng(), latLng.lat()]);
  };

  const attachPolygonDragListener = () => {
    if (!polygonRef.current) return;
  
    polygonRef.current.addListener('dragstart', () => {
      closeInfoWindow();
      polygonPathRef.current.skipValidation = true;
    });
  
    polygonRef.current.addListener('dragend', () => {
      polygonPathRef.current.skipValidation = false;
      updatePolygonPath();
    });
  };

  const updatePolygonPath = () => {
    if (!polygonRef.current) return;

    const path = polygonRef.current.getPath().getArray();
    const newCoordinates = _.map(path, (latLng) => ({ lat: latLng.lat(), lng: latLng.lng() }));
    polygonPathRef.current = path;
    onPolygonChange(newCoordinates);
  };


  const resetPolygon = () => {
    if (polygonRef.current) {
      polygonRef.current.setPath([]);
      polygonPathRef.current = [];
      onPolygonChange([]);
      attachPolygonListeners();
    }
  };

  const doesPolygonSelfIntersect = (coordinates) => {
    const numPoints = coordinates.length;

    for (let i = 0; i < numPoints - 1; i++) {
      const segment1 = lineString([coordinates[i], coordinates[i + 1]]);

      for (let j = i + 2; j < numPoints - (i === 0 ? 1 : 0); j++) {
        const segment2 = lineString([coordinates[j], coordinates[(j + 1) % numPoints]]);

        if (booleanIntersects(segment1, segment2)) {
          showTooltip('その操作はできません');
          return true;
        }
      }
    }
    return false;
  };

  const showTooltip = (message) => {
    closeInfoWindow();
  
    if (!googleMapRef.current) return;
  
    const bounds = googleMapRef.current.getBounds();
    if (!bounds) return;
  
    const position = {
      lat: bounds.getNorthEast().lat(),
      lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
    };
  
    infoWindowRef.current = new window.google.maps.InfoWindow({
      content: `<div style="font-size: 14px; font-weight: bold; color: red;">${message}</div>`,
      position,
      disableAutoPan: true,
      pixelOffset: new window.google.maps.Size(0, 70),
    });
  
    infoWindowRef.current.open(googleMapRef.current);
  
    setTimeout(() => {
      closeInfoWindow();
    }, 3000);
  };
  
  const closeInfoWindow = () => {
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
  };

  const hideCurrentUndoButton = () => {
      polygonRef.current.setOptions({ editable: false });
      polygonRef.current.setOptions({ editable: true }); 
  };

  useEffect(() => {
    if (!googleMapRef.current) return;

    if (drawingMode === DRAWING_MODES.circle.valueName) {
      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
        polygonPathRef.current = [];
        onPolygonChange([]);
      }

      if (circleState.current.center && circleState.current.radius) {
        addCircle(circleState.current.center, circleState.current.radius);
        googleMapRef.current.setCenter(circleState.current.center);
        googleMapRef.current.setZoom(15);
      } else {
        const mapBounds = googleMapRef.current.getBounds();
        const mapCenter = mapBounds
          ? mapBounds.getCenter().toJSON()
          : { lat: 35.6581196, lng: 139.7413643 };
        addCircle(mapCenter, defaultRadius);
        googleMapRef.current.setCenter(mapCenter);
        googleMapRef.current.setZoom(15);
      }
    } else {
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    }
  }, [drawingMode]);

  useEffect(() => {
    if (window.google && window.google.maps && !window.mapError) {
      if (!googleMapRef.current) {
        initMap();
      }
    } else {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAP_KEY}&libraries=places&callback=initMap&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      window.initMap = initMap;
    }
  }, [postalCode]);

  useEffect(() => {
    if (!googleMapRef.current) return;
  
    window.google.maps.event.clearListeners(googleMapRef.current, 'click');
    googleMapRef.current.addListener('click', (e) => {
      if (drawingMode === DRAWING_MODES.circle.valueName) {
        if (circleRef.current) {
          circleRef.current.setMap(null);
        }
        addCircle(e.latLng, defaultRadius);
      } else {
        addPolygonPoint(e.latLng);
      }
    });
  }, [drawingMode]);

  return <div ref={mapRef} id="map" className={`${disabled ? 'disabled' : ''} ${drawingMode === DRAWING_MODES.circle.valueName ? DRAWING_MODES.circle.valueName : DRAWING_MODES.polygon.valueName}`} />;
};

export default GoogleMapComponent;



// WEBPACK FOOTER //
// ./src/components/gmap/googleMapComponent.jsx